import React, { useContext } from "react";
import WiresSettings from "./Wires";
import ActionsContext from "common/ActionsContext";
import help from "help/viewStatusInterfacesDataWires.md";
import { composeApplyCommand, parseWires } from "./api";
import { parseInterfaces } from "views/Status/Interfaces/LinkState/api";
import { enumerate } from "common/utils";
import DetailModal from "views/Status/Interfaces/LinkState/Detail";
import CaptureModal from "views/Status/Interfaces/LinkState/Capture";
import HandleEnableOrDisable from "views/Status/Interfaces/LinkState/HandleEnableOrDisable";
import ReloadingSettingsView from "common/SettingsView/Reloading";
import View from "common/View";
import BqnContext from "common/BqnContext";
import Loading from "common/Loading";
import { atBqnTarget } from "common/api";

const filterNotManagementOrLoopback = (devices) => {
  return devices.filter(
    ({ management, type }) => type !== "loopback" && management !== true
  );
};

export const load = (mode, bqnId) =>
  Promise.all([
    ifCl
      .run(atBqnTarget("show interface raw", mode, bqnId))
      .then(parseInterfaces)
      .then(filterNotManagementOrLoopback),
    ifCl
      .run(atBqnTarget("show pkteng wire", mode, bqnId))
      .then(parseWires),
  ]).then(([interfaces, wires]) => {
    const interfacesMap = interfaces.reduce((acc, interfaceS) => {
      const { name } = interfaceS;
      acc[name] = interfaceS;
      return acc;
    }, {}); 
    const dataWires = wires.reduce((accWire, wire) => {
      const { acc, inet } = wire;
      const { iface } = acc;
      const { iface: ifaceNet } = inet;
      if (interfacesMap[iface]) {
        const { connected, enabled, type } = interfacesMap[iface];
        wire.acc = {
          ...wire.acc,
          ["connected"]: connected,
          ["enabled"]: enabled,
          ["type"]: type,
        };
      }else{
        wire.acc = {
          ...wire.acc,
          ["type"]: 'n/a'
        }
      }
      
      if (interfacesMap[ifaceNet]) {
        const { connected, enabled, type } = interfacesMap[ifaceNet];
        wire.inet = {
          ...wire.inet,
          ["connected"]: connected,
          ["enabled"]: enabled,
          ["type"]: type,
        };
      }else{
        wire.inet = {
          ...wire.inet,
          ["type"]: 'n/a',
        };
      }

      accWire.push(wire);
      return accWire;
    }, []);

    return {
      interfaces,
      wires: enumerate(1, { stored: true })(dataWires),
    };
  });
const CANT_PROCEED_ERROR =
  "Cannot proceed while there are pending config changes." +
  " Try again later.";

const clearConfigAttempt = () =>
  ifCl.run("configure\nclear config changes\n", /*is batch*/ true);

const ShowDataWiresSettings = ({
  defaults,
  onStateUpdate,
}) => (
  <div className="body">
    <div className="row">
      <WiresSettings
        wires={defaults.wires}
        interfaces={defaults.interfaces}
        onChange={(wires) => onStateUpdate({ wires })}
      />
    </div>
  </div>
);

const DataWiresConfig = ({ returnView }) => {
  const { bqnId, clusterStatus } = useContext(BqnContext);
  const mode = clusterStatus?.mode;
  const submitCommand = (command) =>
    ifCl.run(command + "\n", /*is batch*/ true);

  const checkNothingPending = () =>
    ifCl.run("show config diff").then((response) => {
      if (response.length > 0) {
        throw new Error(CANT_PROCEED_ERROR);
      }
    });

  const notifyAndRevert = (error) => {
    error = error.message === undefined ? new Error(error) : error;
    clearConfigAttempt();
    throw error;
  };

  const apply = (settings, originalSettings = {}) =>
    checkNothingPending()
      .then(() => composeApplyCommand(originalSettings, settings))
      .then(submitCommand)
      .catch(notifyAndRevert);

  return (
    <View
      title="Wires Configuration"
      className="container-lg"
      mdHelp={help}
      returnView={returnView}
      options={[{label: "New Wire", signal: "new-wire"}]}
    >
      {/* Nothing is asked while the BQN context has not loaded the BQN
          selected: the commands would not carry its option, and their
          responses would be those of the whole cluster. */}
      {bqnId === undefined ? (
        <Loading />
      ) : (
        <ReloadingSettingsView
          key={`data-wires-config-bqn${bqnId}`}
          load={() => load(mode, bqnId)}
          apply={apply}
          clear={clearConfigAttempt}
          noHeader={true}
          applyActionClassName="hidden-to-operators"
          wrapIntoForm={false}
          subtitle={
            <small>
              Press <code>Apply</code> button at the end of the page to set the
              new configuration values.
            </small>
          }
        >
          <ShowDataWiresSettings />
        </ReloadingSettingsView>
      )}
      <CaptureModal />
      <DetailModal />
      <HandleEnableOrDisable />
    </View>
  );
};

export default DataWiresConfig;
